import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import RootState from '../redux/states/root.state';
import Command from '../classes/command.class';
import useMQTT from './useMQTT';
import useV1Mqtt from './useV1Mqtt';

/**
 * Whether a spa record is a V1 / Coldfire spa (still on the legacy Mosquitto stack).
 * `is_moved` is the signal the whole portal and the mobile app use.
 */
export const isV1Spa = (spa: any): boolean => !!spa && !spa.is_moved;

/**
 * The live connection and `Command` for the spa a control page is showing, whichever
 * generation it is.
 *
 * V2 (Yocto, AWS IoT) spas go through `useMQTT` exactly as before. V1 / Coldfire spas
 * go through `useV1Mqtt`, which fills the same redux slices from the legacy broker and
 * returns a `Command` that speaks the legacy wire format. Both hooks idle on an empty
 * id, so exactly one of them is live.
 *
 * Until `fetchSpa` has returned this spa's record its generation is unknown, so neither
 * hook starts: guessing V2 would subscribe and send `report_connection_status` over AWS
 * IoT for a V1 spa. The control pages show their spinner during that fetch.
 */
const useSpaCommand = (spaId: string): Command => {
    const spa = useSelector((state: RootState) => state.spa.data);
    const isThisSpa = !!spaId && typeof spa?.id === 'string' && spa.id.toLowerCase() === spaId.toLowerCase();
    const v1 = isThisSpa && isV1Spa(spa);

    const [commandTopic] = useMQTT(isThisSpa && !v1 ? spaId : '');
    const v1Command = useV1Mqtt(v1 ? spaId : '');

    return useMemo(
        () => (v1 && v1Command ? v1Command : new Command(commandTopic)),
        [v1, v1Command, commandTopic],
    );
};

export default useSpaCommand;
